import { type PropsWithChildren, type SVGProps, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { BoltFillIcon, MessageDotsFillIcon, MessageDotsIcon, PaperFillIcon, PaperIcon, PersonStackFillIcon, PersonStackIcon, SquareGridFillIcon, SquareGridIcon } from '@/icons';
import { useClassroomId, useCountryCode } from '@/shared/hooks';
import { useUserPlanManager } from '@/shared/plan';
import { cn } from '@/utils';
import { useQuizsetId } from '../../hooks/useQuizsetId';
interface Tab {
  label: string;
  InactiveIcon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  ActiveIcon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  href: string;
  shouldShowProBadge?: boolean;
}
export const Tabs = () => {
  const {
    t
  } = useTranslation(['lms', 'common']);
  const router = useRouter();
  const {
    userPlanManager
  } = useUserPlanManager();
  const {
    isKR
  } = useCountryCode();
  const [showAiTutor, setShowAiTutor] = useState(false);
  useEffect(() => {
    setShowAiTutor(isKR);
  }, [isKR]);
  const canAccessFeature = userPlanManager.canAccessFeature('LMS_PARTIAL');
  const id = useQuizsetId();
  const classroomId = useClassroomId();
  const classroomQuery = classroomId ? `?classroomId=${classroomId}` : '';
  const TABS: Tab[] = [{
    label: t('lms:lms.report.title'),
    InactiveIcon: SquareGridIcon,
    ActiveIcon: SquareGridFillIcon,
    href: `/lms/${id}/learning-report${classroomQuery}`,
    shouldShowProBadge: !canAccessFeature
  }, {
    label: t('lms:lms.questionStats.title'),
    InactiveIcon: PaperIcon,
    ActiveIcon: PaperFillIcon,
    href: `/lms/${id}/question-stats${classroomQuery}`,
    shouldShowProBadge: !canAccessFeature
  }, {
    label: t('lms:lms.studentStats.title'),
    InactiveIcon: PersonStackIcon,
    ActiveIcon: PersonStackFillIcon,
    href: `/lms/${id}/student-stats${classroomQuery}`
  }];
  if (showAiTutor) {
    TABS.push({
      label: t('common:common.aiTutor'),
      InactiveIcon: MessageDotsIcon,
      ActiveIcon: MessageDotsFillIcon,
      href: `/lms/${id}/ai-tutor${classroomQuery}`,
      shouldShowProBadge: !canAccessFeature
    });
  }
  return <nav className="flex flex-col gap-[4px]" data-sentry-component="Tabs" data-sentry-source-file="Tabs.tsx">
      {TABS.map(tab => {
      const isActive = router.asPath.includes(tab.href);
      const Icon = isActive ? tab.ActiveIcon : tab.InactiveIcon;
      return <Tab key={tab.href} href={tab.href} isActive={router.asPath.includes(tab.href)}>
            <Icon className="flex size-[20px] shrink-0" />
            {tab.label}
            <span className="ml-auto">
              {tab.shouldShowProBadge && <LegacyProBadge />}
            </span>
          </Tab>;
    })}
    </nav>;
};
const Tab = ({
  href,
  isActive,
  children
}: PropsWithChildren<{
  href: string;
  isActive: boolean;
}>) => <Link href={href} className={cn('flex gap-xs py-sm px-xs rounded-[8px] text-body-lg-semibold text-neutral', isActive && 'bg-opacity-violet-2 text-primary')} data-sentry-element="Link" data-sentry-component="Tab" data-sentry-source-file="Tabs.tsx">
    {children}
  </Link>;
const LegacyProBadge = () => {
  return <div className="flex items-center rounded-[32px] border-[0.5px] border-[rgba(28,27,31,0.04)] bg-gradient-to-r from-[#8478FF] to-[#3DA9FF] px-3xs py-4xs text-white" data-sentry-component="LegacyProBadge" data-sentry-source-file="Tabs.tsx">
      <BoltFillIcon size="2xs" fill="#FFF" data-sentry-element="BoltFillIcon" data-sentry-source-file="Tabs.tsx" />
      <span className="text-caption-md-semibold text-white">Pro</span>
    </div>;
};